import { Button, Card, Group, LoadingOverlay, Stack, Switch, Table, TagsInput, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import ideiaSchema from "@/modules/ideias/schema/ideia-schema";
import useUserStore from "@/modules/users/store";
import {
  TConfiguracaoFormularioIdeias,
  getDefaultConfiguracaoFormularioIdeias,
  saveConfiguracaoFormularioIdeias,
  CAMPOS_CONFIGURAVEIS_IDEIA,
  useConfiguracaoFormularioIdeias
} from "../index";

/**
 * Componente de formulário para configuração do formulário de ideias
 */
export default function ConfiguracaoFormularioIdeiasForm() {
  const activeEmpresaId = useUserStore((store) => store.activeEmpresaId);
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const { data: configuracao, isLoading } = useConfiguracaoFormularioIdeias();
  const schemaDescription = useMemo(() => ideiaSchema.describe(), []);
  const campoDescricaoOriginalMap = useMemo(() => {
    const map: Record<(typeof CAMPOS_CONFIGURAVEIS_IDEIA)[number], string> = Object.fromEntries(
      CAMPOS_CONFIGURAVEIS_IDEIA.map((campo) => [campo, ""])
    ) as Record<(typeof CAMPOS_CONFIGURAVEIS_IDEIA)[number], string>;
    for (const campo of CAMPOS_CONFIGURAVEIS_IDEIA) {
      const schemaField = schemaDescription.fields[campo] as { label?: string; meta?: { description?: string } };
      map[campo] = schemaField?.meta?.description || schemaField?.label || campo;
    }
    return map;
  }, [schemaDescription]);

  const form = useForm<TConfiguracaoFormularioIdeias>({
    initialValues: getDefaultConfiguracaoFormularioIdeias()
  });

  useEffect(() => {
    if (configuracao) {
      form.setValues(configuracao);
    }
  }, [configuracao]);

  const handleRestoreClick = useCallback(
    (campo: (typeof CAMPOS_CONFIGURAVEIS_IDEIA)[number]) => {
      const campoDescricaoOriginal = campoDescricaoOriginalMap[campo];
      form.setFieldValue(`${campo}.titulo`, campoDescricaoOriginal);
      form.setFieldValue(`${campo}.visivel`, true);
    },
    [campoDescricaoOriginalMap, form]
  );

  const handleVisibilityChange = useCallback(
    (campo: (typeof CAMPOS_CONFIGURAVEIS_IDEIA)[number], checked: boolean) => {
      form.setFieldValue(`${campo}.visivel`, checked);
    },
    [form]
  );

  /**
   *
   * @param values
   */
  async function handleSubmit(values: TConfiguracaoFormularioIdeias) {
    if (!activeEmpresaId) return;

    try {
      setIsSaving(true);
      const configuracaoComTitulosPadrao: TConfiguracaoFormularioIdeias = {} as TConfiguracaoFormularioIdeias;
      for (const campo of CAMPOS_CONFIGURAVEIS_IDEIA) {
        const titulo = values[campo]?.titulo?.trim();
        configuracaoComTitulosPadrao[campo] = {
          ...values[campo],
          titulo: titulo || campoDescricaoOriginalMap[campo]
        };
      }

      await saveConfiguracaoFormularioIdeias(activeEmpresaId, {
        ...configuracaoComTitulosPadrao,
        objetivos: values.objetivos
      });

      await queryClient.invalidateQueries({
        queryKey: ["configuracaoFormularioIdeias", activeEmpresaId]
      });

      notifications.show({
        title: "Sucesso",
        message: "Configuração salva com sucesso!",
        color: "green",
        icon: <IconCheck />
      });
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
      notifications.show({
        title: "Erro",
        message: "Erro ao salvar configuração do formulário",
        color: "red",
        icon: <IconX />
      });
    } finally {
      setIsSaving(false);
    }
  }

  const getRestoreHandler = useCallback(
    (campo: (typeof CAMPOS_CONFIGURAVEIS_IDEIA)[number]) => () => handleRestoreClick(campo),
    [handleRestoreClick]
  );

  const getVisibilityChangeHandler = useCallback(
    (campo: (typeof CAMPOS_CONFIGURAVEIS_IDEIA)[number]) => (event: React.ChangeEvent<HTMLInputElement>) =>
      handleVisibilityChange(campo, event.currentTarget.checked),
    [handleVisibilityChange]
  );

  return (
    <Card withBorder shadow="md" style={{ position: "relative" }} styles={{ section: { overflow: "auto" } }}>
      <LoadingOverlay visible={isLoading} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Card.Section withBorder p={0}>
          <Table.ScrollContainer minWidth={540}>
            <Table highlightOnHover striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: "100%" }}>Campo / Título Customizado</Table.Th>
                  <Table.Th style={{ textAlign: "center", whiteSpace: "nowrap", width: "120px" }}>Visível</Table.Th>
                  <Table.Th style={{ textAlign: "center", whiteSpace: "nowrap", width: "140px" }}>Restaurar</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {CAMPOS_CONFIGURAVEIS_IDEIA.map((campo) => {
                  const campoDescricaoOriginal = campoDescricaoOriginalMap[campo];
                  return (
                    <Table.Tr key={campo}>
                      <Table.Td style={{ width: "100%" }}>
                        <Stack gap="xs" w="100%">
                          {/*  <Text fw={500}>{fieldLabel}</Text> */}
                          <TextInput
                            placeholder={campoDescricaoOriginal}
                            {...form.getInputProps(`${campo}.titulo`)}
                            disabled={!form.values[campo]?.visivel}
                          />
                        </Stack>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <Group justify="center" gap="xs">
                          <Switch
                            checked={form.values[campo]?.visivel ?? true}
                            onChange={getVisibilityChangeHandler(campo)}
                          />
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <Group justify="center" gap="xs">
                          <Button variant="light" size="xs" onClick={getRestoreHandler(campo)}>
                            Restaurar
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card.Section>

        <Card.Section inheritPadding py="md" withBorder>
          <TagsInput
            label="Objetivos Personalizados"
            description="Digite um objetivo e pressione Enter para adicioná-lo. Você pode remover um objetivo clicando no x."
            placeholder="Adicionar objetivo e pressionar Enter"
            {...form.getInputProps("objetivos")}
            clearable
          />
        </Card.Section>

        <Card.Section inheritPadding py="md" withBorder>
          <Group justify="flex-end">
            <Button type="submit" loading={isSaving}>
              Salvar Configuração
            </Button>
          </Group>
        </Card.Section>
      </form>
    </Card>
  );
}
